import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import repayment from '../../assets/images/repayment.png';
import masterdata from '../../assets/images/master-data.png';
import institute from '../../assets/images/institute.png';
import emis from '../../assets/images/createmanual.png';
import overdue from '../../assets/images/overdue.png';
import roles from '../../assets/images/roles.png';
import box from '../../assets/images/box.png';
import loans from '../../assets/images/loans.png';
import statusIcon from '../../assets/images/statusIcon.png';
import rules from '../../assets/images/rules.jpg';
import TagIcon from '../../assets/images/Tagicon.png';
import calculator from '../../assets/images/notificationIcons/calculator.png';
import PartnerImg2 from '../../assets/images/partnerImg2.png';
import partnerImg1 from '../../assets/images/partnerImg1.png';

import {
  hasRole,
  excludesRoles,
  matchesAny,
  matchesAll,
  negate,
  hasClientId,
  hasInstituteId,
  isNotPersonalLoan,
} from './visibility';

const BIJAY_CLIENT_ID = '98ffb18a-9fee-43a5-91a3-4b2b9d9ae361';
const NO_REPORTS_INSTITUTE_ID = '9d5eca4b-54f7-42a7-8794-1413e00b462e';

// Every status-count leaf hides its badge for these roles — confirmed identical
// across all 10 links in the pre-refactor StatusLinks component.
const HIDE_BADGE_ROLES = ['institute_user', 'institute_admin', 'channel_user'];

const withDefaultBadgeVisibility = (items) =>
  items.map((item) => ({ hideBadgeForRoles: HIDE_BADGE_ROLES, ...item }));

const clearSessionKeys = (...keys) => () => {
  keys.forEach((key) => sessionStorage.removeItem(key));
};

const statusChildren = withDefaultBadgeVisibility([
  {
    key: 'status-logins',
    label: 'Logins',
    path: '/logins',
    visible: matchesAll(
      negate(hasClientId(BIJAY_CLIENT_ID)),
      matchesAny(hasRole('admin'), hasRole('cra'), hasRole('crsa')),
    ),
  },
  {
    key: 'status-submission',
    label: 'In Submission',
    path: '/status/Submission',
    badgeStatusKey: 'In Submission',
    onBeforeNavigate: clearSessionKeys('TableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-review',
    label: 'In Review',
    path: '/status/Review',
    badgeStatusKey: 'In Review',
    onBeforeNavigate: clearSessionKeys('TableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-process',
    label: 'In Process',
    path: '/status/Process',
    badgeStatusKey: 'In Process',
    onBeforeNavigate: clearSessionKeys('processingTableFilters'),
  },
  {
    key: 'status-sanction',
    label: 'In Sanction',
    path: '/status/Sanction',
    badgeStatusKey: 'In Sanction',
    onBeforeNavigate: clearSessionKeys('sanctionTableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-disbursement',
    label: 'In Disbursement',
    path: '/status/Disbursement',
    badgeStatusKey: 'Ready to Disburse',
    onBeforeNavigate: clearSessionKeys('readyToDisburseFilters', 'sanctionProductType'),
  },
  {
    key: 'status-pending',
    label: 'Pending Approvals',
    path: '/status/Pending',
    badgeStatusKey: 'Pending Disbursement',
    visible: excludesRoles('institute_user', 'institute_admin', 'channel_user', 'crsa', 'cra'),
  },
  {
    key: 'status-disbursed',
    label: 'Disbursed',
    path: '/status/Disbursed',
    badgeStatusKey: 'Disbursed',
    onBeforeNavigate: clearSessionKeys('disbursedTableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-dropped',
    label: 'Dropped',
    path: '/status/Dropped',
    badgeStatusKey: 'Dropped',
    onBeforeNavigate: clearSessionKeys('TableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-rejected',
    label: 'Rejected',
    path: '/status/Rejected',
    badgeStatusKey: 'Rejected',
    onBeforeNavigate: clearSessionKeys('TableFilters', 'sanctionProductType'),
  },
  {
    key: 'status-hold',
    label: 'On Hold',
    path: '/status/Hold',
    badgeStatusKey: 'On Hold',
    onBeforeNavigate: clearSessionKeys('TableFilters', 'sanctionProductType'),
  },
]);

const portfolioChildren = [
  { key: 'portfolio-analytics', label: 'Analytics', path: '/analytics' },
  { key: 'portfolio-api-analytics', label: 'Api-Analytics', path: '/apianalytics' },
  { key: 'portfolio-api-ratecard', label: 'API Rate Card', path: '/apiratecard' },
  { key: 'portfolio-cuts', label: 'Portfolio Cuts', path: '/Portfoliocuts' },
  { key: 'portfolio-pooltag', label: 'PoolTag report', path: '/PoolTagedreport' },
  { key: 'portfolio-institute-dashboard', label: 'Institute Dashboard', path: '/Demographical' },
  { key: 'portfolio-bounce-analytics', label: 'Bounce Analytics', path: '/Mandatepresentdata' },
  { key: 'portfolio-notification-dashboard', label: 'Notification Dashboard', path: '/NotificationReport' },
  { key: 'portfolio-discount-report', label: 'Discount Report', path: '/DiscountReport' },
  { key: 'portfolio-fibe-billing', label: 'Fibe Commission Billing', path: '/FibeCommissionBilling' },
].map((item) => ({
  visible: matchesAll(
    excludesRoles('institute_user', 'institute_admin', 'channel_user'),
    negate(hasClientId(BIJAY_CLIENT_ID)),
  ),
  ...item,
})).concat([
  {
    key: 'portfolio-master-data',
    label: 'Master Data',
    path: '/reports',
    visible: excludesRoles('institute_user', 'institute_admin', 'channel_user'),
  },
]);

const collectionsChildren = [
  { key: 'collections-demand', label: 'Demand Reports', path: '/DemandReport' },
  { key: 'collections-disbursements', label: 'Disbursements', path: '/Monthlydisbursements' },
  { key: 'collections-par', label: 'Par Reports', path: '/MonthlyPAR' },
  { key: 'collections-institute-par', label: 'Institute Par Reports', path: '/InstitutePAR' },
  { key: 'collections-performance', label: 'Collection Performance', path: '/CollectionPerformance' },
  {
    key: 'collections-incentive',
    label: 'Collection Incentive',
    path: '/collection-incentive',
    visible: matchesAny(hasRole('admin'), hasRole('collection')),
  },
  {
    key: 'collections-resolution',
    label: 'Resolution DPD',
    path: '/resolution',
    visible: excludesRoles('institute_user', 'institute_admin', 'channel_user'),
    onBeforeNavigate: () => sessionStorage.setItem('selectedTabIndex', 0),
  },
  {
    key: 'collections-rollback',
    label: 'RollRate Report',
    path: '/RollBackReport',
    visible: excludesRoles('institute_user', 'institute_admin', 'channel_user'),
    onBeforeNavigate: () => sessionStorage.setItem('selectedTabIndex', 0),
  },
  { key: 'collections-closed', label: 'Closed loans', path: '/Closedloans' },
  { key: 'collections-written-off', label: 'WrittenOff Loans', path: '/WrittenOffLoans' },
  { key: 'collections-legal', label: 'Legal report', path: '/Legalreport' },
  { key: 'collections-mandate-paused', label: 'Mandatepaused report', path: '/Mandatepausedreport' },
].map((item) => ({
  visible: excludesRoles('institute_user', 'institute_admin', 'channel_user'),
  ...item,
}));

const lmsBaseVisible = excludesRoles('institute_user', 'institute_admin', 'channel_user');

const lmsChildren = [
  { key: 'lms-loans', label: 'Loans', path: '/Loans', icon: loans, iconType: 'img', visible: lmsBaseVisible },
  { key: 'lms-repayments', label: 'Repayments', path: '/Repayments', icon: repayment, iconType: 'img', visible: lmsBaseVisible },
  { key: 'lms-emis', label: "EMI's", path: '/Emis', icon: emis, iconType: 'img', visible: lmsBaseVisible },
  { key: 'lms-overdue', label: 'Overdue', path: '/Overdue', icon: overdue, iconType: 'img', visible: lmsBaseVisible },
  {
    key: 'lms-refunds',
    label: 'Refunds',
    path: '/RefundsTracker',
    icon: PendingActionsIcon,
    iconType: 'mui',
    visible: matchesAll(lmsBaseVisible, isNotPersonalLoan),
    onBeforeNavigate: clearSessionKeys('refundsTrackerFilters'),
  },
  {
    key: 'lms-settlements',
    label: 'Settlements',
    path: '/Settlements',
    icon: PendingActionsIcon,
    iconType: 'mui',
    visible: matchesAll(
      lmsBaseVisible,
      isNotPersonalLoan,
      matchesAny(hasRole('finance'), hasRole('admin')),
      negate(hasClientId(BIJAY_CLIENT_ID)),
    ),
  },
];

const adminBaseVisible = excludesRoles('institute_user', 'institute_admin', 'channel_user');
const adminPartnerVisible = matchesAll(matchesAny(hasRole('crm'), hasRole('admin')), isNotPersonalLoan);

const adminChildren = [
  { key: 'admin-products', label: 'Products', path: '/products', icon: box, iconType: 'img', visible: adminBaseVisible },
  { key: 'admin-coupons', label: 'Coupons', path: '/coupons', icon: LocalOfferIcon, iconType: 'mui', visible: hasRole('admin') },
  {
    key: 'admin-mci-search',
    label: 'MCIsearch',
    path: '/MCIsearch',
    icon: MedicalInformationIcon,
    iconType: 'mui',
    visible: matchesAll(adminBaseVisible, isNotPersonalLoan),
  },
  {
    key: 'admin-institute',
    label: 'Institutes',
    path: '/Institute',
    icon: institute,
    iconType: 'img',
    visible: matchesAll(adminBaseVisible, isNotPersonalLoan),
  },
  {
    key: 'admin-rules',
    label: 'Rule Engine',
    path: '/rules',
    icon: rules,
    iconType: 'img',
    visible: matchesAll(adminBaseVisible, isNotPersonalLoan),
  },
  {
    key: 'admin-rinux-rules',
    label: 'RINUX Credit Rules',
    path: '/rinux-rules',
    icon: rules,
    iconType: 'img',
    visible: hasRole('admin'),
  },
  {
    key: 'admin-loan-schedule',
    label: 'APR Simulator',
    path: '/LoanSchedule',
    icon: calculator,
    iconType: 'img',
    visible: matchesAny(hasRole('crm'), hasRole('cra'), hasRole('crsa'), hasRole('admin')),
  },
  { key: 'admin-partners', label: 'Partners', path: '/Partners', icon: PartnerImg2, iconType: 'img', visible: adminPartnerVisible },
  { key: 'admin-anchors', label: 'Anchors', path: '/Anchors', icon: partnerImg1, iconType: 'img', visible: adminPartnerVisible },
  { key: 'admin-active-banks', label: 'Activebanks', path: '/Activebanks', icon: AccountBalanceIcon, iconType: 'mui', visible: adminPartnerVisible },
  { key: 'admin-pool-tagging', label: 'Pooltag', path: '/PoolTagging/', icon: TagIcon, iconType: 'img', visible: adminPartnerVisible },
  { key: 'admin-roles', label: 'Roles', path: '/roles', icon: roles, iconType: 'img', visible: matchesAny(hasRole('crm'), hasRole('admin')) },
  {
    key: 'admin-ai-credits',
    label: 'AI Credits',
    path: '/ai-credits',
    icon: '⚡',
    iconType: 'emoji',
    visible: matchesAny(hasRole('admin'), hasRole('super_admin'), hasRole('crm')),
  },
  {
    key: 'admin-fund-management',
    label: 'Fund Management',
    path: '/fund-management',
    icon: AccountBalanceWalletIcon,
    iconType: 'mui',
    visible: matchesAny(hasRole('admin'), hasRole('fund_manager'), hasRole('fund_user')),
  },
  {
    key: 'admin-settings',
    label: 'Admin Settings',
    path: '/admin-settings',
    icon: '⚙️',
    iconType: 'emoji',
    visible: hasRole('admin'),
  },
];

// Root-level nav tree, rendered top to bottom under the logo / New Lead / User Search.
export const navConfig = [
  {
    key: 'status',
    label: 'Status',
    icon: statusIcon,
    iconType: 'img',
    visible: excludesRoles('collection_user'),
    // Clicking "Status" while already institute_user/institute_admin always
    // forces it open (never collapses) — preserved from the original toggleDropdown.
    forceOpenForRoles: ['institute_user', 'institute_admin'],
    children: statusChildren,
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: AppRegistrationIcon,
    iconType: 'mui',
    visible: excludesRoles('institute_user', 'institute_admin', 'institute_cra', 'channel_user', 'cra', 'crsa', 'collection_user'),
    children: [
      { key: 'reports-portfolio', label: 'Portfolio', children: portfolioChildren },
      { key: 'reports-collections', label: 'Loans', children: collectionsChildren },
    ],
  },
  {
    key: 'lms',
    label: 'LMS',
    icon: Diversity2Icon,
    iconType: 'mui',
    visible: matchesAny(
      matchesAll(excludesRoles('institute_user', 'institute_admin', 'institute_cra', 'channel_user', 'cra', 'crsa')),
      hasRole('collection_user'),
    ),
    children: lmsChildren,
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: ManageAccountsIcon,
    iconType: 'mui',
    visible: excludesRoles('institute_user', 'institute_admin', 'channel_user', 'collection_user'),
    children: adminChildren,
  },
  {
    key: 'reports-leaf',
    label: 'Reports',
    icon: masterdata,
    iconType: 'img',
    path: '/reports',
    visible: matchesAny(
      hasRole('institute_admin'),
      hasRole('channel_user'),
      matchesAll(hasRole('institute_user'), (ctx) => ctx.instituteId !== NO_REPORTS_INSTITUTE_ID),
    ),
  },
  {
    key: 'emis-leaf',
    label: 'Emis',
    icon: emis,
    iconType: 'img',
    path: '/Emi',
    visible: matchesAll(hasInstituteId(NO_REPORTS_INSTITUTE_ID), excludesRoles('institute_user', 'institute_cra')),
  },
];

export { BIJAY_CLIENT_ID, NO_REPORTS_INSTITUTE_ID };
export const ExpandArrowIcon = KeyboardArrowRightIcon;
